import { fusionFor, SIGNATURE_RESOURCE } from '../data/classDesign.ts';
import type { FusionPassive } from '../data/classDesign.ts';
import { SUNDER_STRIP, SUBSPECS } from '../data/subspecs.ts';
import type { AbilityDef, SubSpecDef } from '../data/subspecs.ts';
import {
  LEVEL_CAP,
  RULE_SLOTS,
  SLOT_IDS,
  SLOT_MAX_RANK,
  SLOT_WEIGHT,
  type ArchetypeId,
  type BuildState,
  type Condition,
  type EquippedGear,
  type Item,
  type Rule,
  type SlotId,
  type SubSpecId,
} from '../model/types.ts';

export interface RuntimeAbility {
  id: string;
  name: string;
  kind: AbilityDef['kind'];
  coeff: number;
  cooldownTicks: number;
  /** Armor stacks removed when this ability hits. */
  strip: number;
  ignoreDr: number;
  slot: SlotId;
  specId: SubSpecId;
}

export interface ResolvedHero {
  level: number;
  maxHp: number;
  attack: number;
  armor: number;
  armorBreak: number;
  healPower: number;
  abilities: RuntimeAbility[];
  rules: Rule[];
  /** Extra stacks removed by Frayed Edge. */
  bonusStrip: number;
  affinity: 'pure' | 'hybrid' | 'triad' | 'open';
  heart: ArchetypeId | null;
  fusionName: string | null;
  fusionText: string | null;
  /** Heart Mark signature resource. Pure is +10%. A triad is −5%. */
  resourcePool: number;
  resourceName: string | null;
  /** Living Bastion: share of blocked damage returned as a heal. */
  groveHeal: number;
  /** Borrowed Time: a heal is recorded as Debt and spent on the next hit. */
  debtOnHeal: boolean;
}

export function rankInSlot(build: BuildState, slot: SlotId): number {
  const spec = build[slot];
  if (!spec) return 0;
  const raw = build.ranks[spec] ?? 0;
  return Math.max(0, Math.min(SLOT_MAX_RANK[slot], raw));
}

export function slotFactor(slot: SlotId, rank: number): number {
  const clamped = Math.max(0, Math.min(SLOT_MAX_RANK[slot], rank));
  return SLOT_WEIGHT[slot] * (1 + clamped * 0.08);
}

export function affinityOf(build: BuildState): ResolvedHero['affinity'] {
  const specs = equippedSpecs(build);
  if (specs.length < 3) return 'open';
  const archetypes = new Set(specs.map((s) => s.archetype));
  if (archetypes.size === 1) return 'pure';
  if (archetypes.size === 3) return 'triad';
  return 'hybrid';
}

/** Hybrid 2+1 braid. The Heart Mark's fusion variant picks the line. */
export function activeFusion(build: BuildState): FusionPassive | null {
  if (affinityOf(build) !== 'hybrid') return null;
  const heartId = build.primary;
  if (!heartId) return null;
  const heart = SUBSPECS[heartId];
  const other = equippedSpecs(build)
    .map((spec) => spec.archetype)
    .find((archetype) => archetype !== heart.archetype);
  if (!other) return null;
  return fusionFor(heart.archetype, other, heart.fusionVariant);
}

/** Pure braids multiply health and attack by this. */
export const PURE_STAT_MULTIPLIER = 1.1;
/** Pure braids multiply the Heart resource pool by this. */
export const PURE_POOL_MULTIPLIER = 1.1;
/** Three-archetype braids multiply the Heart resource pool by this. */
export const TRIAD_POOL_MULTIPLIER = 0.95;

/** §2.6 resource pool. Level 1 starts at 40. Pure +10%. Triad −5%. */
export function resourcePoolFor(level: number, affinity: ResolvedHero['affinity']): number {
  const base = 40 + (Math.max(1, Math.floor(level)) - 1) * 2;
  if (affinity === 'pure') return Math.round(base * PURE_POOL_MULTIPLIER);
  if (affinity === 'triad') return Math.round(base * TRIAD_POOL_MULTIPLIER);
  return base;
}

export function equippedSpecs(build: BuildState): SubSpecDef[] {
  const out: SubSpecDef[] = [];
  for (const slot of SLOT_IDS) {
    const id = build[slot];
    if (id) out.push(SUBSPECS[id]);
  }
  return out;
}

/** True when the Heart's catalog line for this 2+1 braid is Frayed Edge. */
export function hasFrayedEdge(build: BuildState): boolean {
  return activeFusion(build)?.name === 'Frayed Edge';
}

function runtimeAbility(spec: SubSpecDef, ability: AbilityDef, slot: SlotId): RuntimeAbility {
  return {
    id: ability.id,
    name: ability.name,
    kind: ability.kind,
    coeff: ability.coeff,
    cooldownTicks: ability.cooldownTicks,
    strip: ability.kind === 'sunder' ? SUNDER_STRIP[slot] : 0,
    ignoreDr: ability.ignoreDr,
    slot,
    specId: spec.id,
  };
}

function abilitiesFor(spec: SubSpecDef, slot: SlotId): RuntimeAbility[] {
  const picked: AbilityDef[] =
    slot === 'primary'
      ? [...spec.abilities, spec.ultimate]
      : slot === 'secondary'
        ? [...spec.abilities]
        : [spec.abilities[0]];
  return picked.map((ability) => runtimeAbility(spec, ability, slot));
}

/** Triad braids ready the Hand Mark ultimate in the flexible skill slot. */
function flexibleAbility(build: BuildState): RuntimeAbility | null {
  if (affinityOf(build) !== 'triad') return null;
  const id = build.secondary;
  if (!id) return null;
  return runtimeAbility(SUBSPECS[id], SUBSPECS[id].ultimate, 'secondary');
}

export function abilitiesOnBar(build: BuildState): RuntimeAbility[] {
  const abilities: RuntimeAbility[] = [];
  for (const slot of SLOT_IDS) {
    const id = build[slot];
    if (!id) continue;
    abilities.push(...abilitiesFor(SUBSPECS[id], slot));
  }
  const extra = flexibleAbility(build);
  if (extra && !abilities.some((ability) => ability.id === extra.id)) abilities.push(extra);
  return abilities;
}

function enhanceMult(item: Item): number {
  return 1 + item.enhance * 0.08;
}

export function resolveHero(args: {
  level: number;
  build: BuildState;
  gear: EquippedGear;
  rules?: Rule[];
}): ResolvedHero {
  const level = Math.max(1, Math.min(LEVEL_CAP, Math.floor(args.level)));
  let hp = 90 + (level - 1) * 18;
  let attack = 10 + (level - 1) * 2;
  let armor = 4 + (level - 1) * 0.5;
  let armorBreak = 0;
  let heal = 6 + (level - 1) * 0.4;

  for (const slot of SLOT_IDS) {
    const id = args.build[slot];
    if (!id) continue;
    const spec = SUBSPECS[id];
    const factor = slotFactor(slot, rankInSlot(args.build, slot));
    hp += spec.profile.hp * factor;
    attack += spec.profile.attack * factor;
    armor += spec.profile.armor * factor;
    armorBreak += spec.profile.armorBreak * factor;
    heal += spec.profile.heal * factor;
  }

  for (const item of Object.values(args.gear)) {
    if (!item) continue;
    const m = enhanceMult(item);
    hp += item.stats.hp * m;
    attack += item.stats.attack * m;
    armor += item.stats.armor * m;
    armorBreak += item.stats.armorBreak * m;
    heal += item.stats.heal * m;
  }

  const affinity = affinityOf(args.build);
  const heartId = args.build.primary;
  const heart = heartId ? SUBSPECS[heartId].archetype : null;
  if (affinity === 'pure') {
    hp *= PURE_STAT_MULTIPLIER;
    attack *= PURE_STAT_MULTIPLIER;
  }
  const fusion = activeFusion(args.build);
  const edge = fusion?.name === 'Frayed Edge';

  return {
    level,
    maxHp: Math.round(hp),
    attack: Math.round(attack),
    armor: Math.round(armor),
    armorBreak: Math.round(armorBreak),
    healPower: Math.round(heal),
    abilities: abilitiesOnBar(args.build),
    rules: sanitizeRules(args.build, args.rules ?? defaultRules(args.build)),
    bonusStrip: edge ? 1 : 0,
    affinity,
    heart,
    fusionName: fusion?.name ?? null,
    fusionText: fusion?.text ?? null,
    resourcePool: resourcePoolFor(level, affinity),
    resourceName: heart ? SIGNATURE_RESOURCE[heart] : null,
    groveHeal: fusion?.name === 'Living Bastion' ? 0.25 : 0,
    debtOnHeal: fusion?.name === 'Borrowed Time',
  };
}

export function defaultRules(build: BuildState): Rule[] {
  const presets: { priority: number; rule: Rule }[] = [];
  for (const slot of SLOT_IDS) {
    const id = build[slot];
    if (!id) continue;
    const spec = SUBSPECS[id];
    const onBar = new Set(abilitiesFor(spec, slot).map((a) => a.id));
    if (!onBar.has(spec.preset.abilityId)) continue;
    presets.push({
      priority: spec.preset.priority,
      rule: {
        id: `preset-${spec.id}`,
        condition: spec.preset.condition,
        abilityId: spec.preset.abilityId,
        target: spec.preset.target,
      },
    });
  }
  presets.sort((a, b) => a.priority - b.priority);
  return presets.slice(0, RULE_SLOTS).map((p) => p.rule);
}

export function barIds(build: BuildState): Set<string> {
  return new Set(abilitiesOnBar(build).map((ability) => ability.id));
}

export function sanitizeRules(build: BuildState, rules: Rule[]): Rule[] {
  const allowed = barIds(build);
  const clean: Rule[] = [];
  for (const rule of rules) {
    if (!allowed.has(rule.abilityId)) continue;
    clean.push({
      id: rule.id,
      abilityId: rule.abilityId,
      target: rule.target,
      condition: clampCondition(rule.condition),
    });
    if (clean.length >= RULE_SLOTS) break;
  }
  return clean;
}

function clampCondition(condition: Condition): Condition {
  if (condition.kind === 'selfHpBelow') {
    return { kind: 'selfHpBelow', pct: clamp(condition.pct, 5, 95) };
  }
  if (condition.kind === 'enemyCountAtLeast') {
    return { kind: 'enemyCountAtLeast', count: clamp(Math.round(condition.count), 1, 6) };
  }
  return condition;
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export function heroPower(hero: Pick<ResolvedHero, 'attack' | 'armor' | 'maxHp' | 'healPower'>): number {
  return Math.round(hero.attack * 4 + hero.armor * 2 + hero.maxHp * 0.25 + hero.healPower * 2);
}

/** Levels 1–8 stay on the old line so the opening push still arrives at the walls together. */
export const XP_CURVE_FLAT_UNTIL = 8;

/**
 * After level 8, each level multiplies the old line by this, once per level past 8.
 * 1.393 puts a max-speed climb, with a 0.3s round trip on every clear, near 10 hours
 * and inside 8–10. SQL reads the same value from `private.xp_curve_growth()`.
 */
export const XP_CURVE_GROWTH = 1.393;

/**
 * Full clear XP while the hero is at most this many levels above the stage.
 * Stage level is the stage id (stage 9 is level-9 content).
 */
export const XP_FALLOFF_BAND = 8;

/** Levels past the band slide linearly down to the floor. */
export const XP_FALLOFF_SPAN = 10;

/**
 * Lowest XP fraction once a stage is XP_FALLOFF_BAND + XP_FALLOFF_SPAN under the hero.
 * 0.5 means a late clear pays half, not a tenth.
 */
export const XP_FALLOFF_FLOOR = 0.5;

/**
 * Hundredths dropped per level past the band. (100 - floor hundredths) / span.
 * Floor 0.5 over 10 levels is 5 hundredths a step, landing on 50.
 */
export const XP_FALLOFF_STEP = 5;

/**
 * XP to leave `level`. Levels 1–8 match the old `15 + 8 * level` line.
 * After that, `round((15 + 8 * level) * XP_CURVE_GROWTH^(level - 8))`.
 * A level below 1 is treated as level 1. The cap and a non-number return 0.
 */
export function xpToNext(level: number): number {
  if (!Number.isFinite(level)) return 0;
  const whole = Math.floor(level);
  if (whole >= LEVEL_CAP) return 0;
  const at = Math.max(1, whole);
  const linear = 15 + at * 8;
  if (at <= XP_CURVE_FLAT_UNTIL) return linear;
  return Math.round(linear * XP_CURVE_GROWTH ** (at - XP_CURVE_FLAT_UNTIL));
}

/**
 * Falloff in hundredths (100 = full XP, 50 = the floor).
 * Steps of 5 hundredths stay on integers and match the SQL function.
 */
export function xpFalloffHundredths(heroLevel: number, stageId: number): number {
  const stageLevel = Math.max(1, Math.floor(stageId) || 1);
  const delta = Math.floor(heroLevel) - stageLevel;
  if (delta <= XP_FALLOFF_BAND) return 100;
  const steps = Math.min(XP_FALLOFF_SPAN, delta - XP_FALLOFF_BAND);
  return 100 - XP_FALLOFF_STEP * steps;
}

/** 1 inside the band, then a straight slide to the floor. At or below the stage, full XP. */
export function xpFalloff(heroLevel: number, stageId: number): number {
  return xpFalloffHundredths(heroLevel, stageId) / 100;
}

/** Whole XP after falloff. Defeat rewards and zero stay zero. */
export function scaledXp(amount: number, heroLevel: number, stageId: number): number {
  const raw = Math.max(0, Math.floor(amount));
  return Math.floor((raw * xpFalloffHundredths(heroLevel, stageId)) / 100);
}

/**
 * XP for one clear. The first time a wall breaks, it pays that wall's XP.
 * A later win of the same wall pays the echo's farm stage instead, so
 * resume-push cannot keep the wall's XP band. No farm stage pays nothing.
 */
export function awardedClearXp(args: {
  stageId: number;
  isWall: boolean;
  clearXp: number;
  heroLevel: number;
  /** Highest stage cleared before this fight. */
  bestCleared: number;
  farmStageId: number;
  farmClearXp: number;
}): number {
  const repeatWall = args.isWall && args.stageId <= args.bestCleared;
  if (repeatWall) {
    if (!Number.isInteger(args.farmStageId) || args.farmStageId < 1) return 0;
    return scaledXp(args.farmClearXp, args.heroLevel, args.farmStageId);
  }
  return scaledXp(args.clearXp, args.heroLevel, args.stageId);
}

export function grantXp(
  level: number,
  xp: number,
  unspent: number,
  amount: number,
): { level: number; xp: number; unspent: number; levelsGained: number } {
  let nextLevel = Math.max(1, Math.min(LEVEL_CAP, level));
  let nextXp = Math.max(0, xp) + Math.max(0, Math.floor(amount));
  let points = unspent;
  let gained = 0;
  while (nextLevel < LEVEL_CAP) {
    const need = xpToNext(nextLevel);
    if (nextXp < need) break;
    nextXp -= need;
    nextLevel += 1;
    points += 1;
    gained += 1;
  }
  if (nextLevel >= LEVEL_CAP) nextXp = 0;
  return { level: nextLevel, xp: nextXp, unspent: points, levelsGained: gained };
}
